import {
  React,
  useState,
  useEffect,
  useSelector,
  useNavigate,
  useDispatch,
  postRequestAuthenticated,
} from "../../globals/imports";
import AdminShell from "./AdminShell";
import IntegrationTab from "./Tabs/IntegrationTab";
import BotStudioTab from "./Tabs/BotStudioTab";
import ChannelsAiTab from "./Tabs/ChannelsAiTab";
import SettingsTab from "./Tabs/SettingsTab";
import MembersTab from "./Tabs/MembersTab";
import AnalyticsTab from "./Tabs/AnalyticsTab";
import ChannelsPanel from "./widgets/ChannelsPanel";
import Pricing from "../Home/Pricing";
import OverviewPanel from "./widgets/OverviewPanel";
import RequestTab from "./Tabs/RequestTab";
import InboxTab from "./Tabs/InboxTab";
import NotificationsTab from "./Tabs/NotificationsTab";
import { fetchbusinessCredentials } from "./../../redux/slices/businessSlice";
import { draftGuard } from "../../utils/draftGuard";

const VALID_NAV = [
  "overview",
  "analytics",
  "channels",
  "setup",
  "members",
  "inbox",
  "requests",
  "notifications",
  "integration",
  "bot",
  "billing",
  "settings",
];

const AdminHome = () => {
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const business = useSelector((state) => state.business.business);
  const bizLoading = useSelector((state) => state.business.loading);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [activeNav, setActiveNav] = useState("overview");
  // The slice starts as {} — treat "not yet loaded" distinctly from "loaded
  // with no subscription", otherwise the gate flashes/sticks on Billing before
  // the fetch resolves.
  const hasBusiness = business && Object.keys(business).length > 0;
  // Gating is three-tiered:
  //  1. No subscription          -> only Billing.
  //  2. Subscribed, not onboarded -> Billing + Overview (Overview hosts the
  //     onboarding/setup wizard, so it must be reachable to verify the domain).
  //  3. Domain verified / live   -> the full dashboard unlocks.
  const subscribed = !!business?.current_subscription;
  // "Onboarded" = the onboarding flow reached domain verification. Note we do
  // NOT use business.isVerified here: that flag is set true for any paid
  // business at creation, so it's "active business", not "domain DNS verified".
  // The real domain milestone lives in onboardingState.
  // Domain-first flow: the full dashboard unlocks once the domain is verified —
  // and every state at/after that (draft_ready, community_committed, embed_live,
  // complete) means the domain is already verified.
  const onboarded = [
    "domain_verified",
    "draft_ready",
    "community_committed",
    "embed_live",
    "complete",
  ].includes(business?.onboardingState);

  const allowedNav = !subscribed
    ? ["billing"]
    : onboarded
    ? VALID_NAV
    : ["overview", "billing"];

  const effectiveNav = allowedNav.includes(activeNav)
    ? activeNav
    : allowedNav[0];

  useEffect(() => {
    const applyHash = () => {
      const hash = window.location.hash?.replace("#", "");
      if (VALID_NAV.includes(hash)) setActiveNav(hash);
    };
    applyHash();
    window.addEventListener("hashchange", applyHash);
    return () => window.removeEventListener("hashchange", applyHash);
  }, []);

  useEffect(() => {
    dispatch(fetchbusinessCredentials());
  }, [dispatch]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (!isLoggedIn) {
        navigate(`/get-started?redirect=/admin/dashboard`);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [isLoggedIn, navigate]);

  const handleNav = (key) => {
    // Leaving the Channels AI tab with an uncommitted draft would discard it —
    // confirm first (the draft lives in BuildCommunityStep, which sets the flag).
    if (activeNav === "setup" && key !== "setup" && draftGuard.isDirty()) {
      const ok = window.confirm(
        "Leave Channels AI? Your draft channels haven't been created yet and will be discarded."
      );
      if (!ok) return;
      draftGuard.set(false);
    }
    setActiveNav(key);
    window.history.pushState(null, "", `#${key}`);
  };

  // Unread "Talk to us" messages -> dot on the inbox nav item. Refetched on tab
  // switches (opening the inbox marks convos read, so leaving it clears the dot).
  const [inboxUnread, setInboxUnread] = useState(0);
  useEffect(() => {
    if (!onboarded) return;
    let active = true;
    postRequestAuthenticated("/fetch/brand/conversations", {})
      .then((res) => {
        if (active && res?.success) {
          const total = (res.conversations || []).reduce(
            (n, c) => n + (c.unread || 0),
            0
          );
          setInboxUnread(total);
        }
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [onboarded, effectiveNav]);

  // Unread account notifications -> dot on the Notifications nav item. Recomputed
  // when lastReadNotification changes (opening the tab marks all read).
  const [notifUnread, setNotifUnread] = useState(0);
  useEffect(() => {
    if (!onboarded) return;
    let active = true;
    postRequestAuthenticated("/fetch/business/notifications", {})
      .then((res) => {
        if (!active || !res?.success) return;
        const lastRead = business?.lastReadNotification
          ? new Date(business.lastReadNotification)
          : null;
        const count = (res.notifications || []).filter((n) =>
          lastRead ? new Date(n.createdAt) > lastRead : true
        ).length;
        setNotifUnread(count);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [onboarded, effectiveNav, business?.lastReadNotification]);

  const renderContent = () => {
    switch (effectiveNav) {
      case "overview":
        return (
          <OverviewPanel
            business={business}
            onContinue={() => navigate("/admin/setup")}
          />
        );
      case "analytics":
        return <AnalyticsTab />;
      case "channels":
        return <ChannelsPanel />;
      case "members":
        return <MembersTab business={business} />;
      case "inbox":
        return <InboxTab />;
      case "requests":
        return <RequestTab />;
      case "notifications":
        return <NotificationsTab onNavChange={handleNav} />;
      case "integration":
        return <IntegrationTab business={business} />;
      case "bot":
        return <BotStudioTab />;
      case "setup":
        return <ChannelsAiTab />;
      case "billing":
        return <Pricing />;
      case "settings":
        return <SettingsTab />;
      default:
        return (
          <OverviewPanel
            business={business}
            onContinue={() => navigate("/admin/setup")}
          />
        );
    }
  };

  // Don't render the (Billing-only) gate until the business has loaded — avoids
  // flashing/sticking on Billing while the first fetch is still in flight.
  if (!hasBusiness && bizLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-theme-primaryBackground">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-theme-chatDivider border-t-theme-accent" />
      </div>
    );
  }

  return (
    <AdminShell
      activeNav={effectiveNav}
      onNavChange={handleNav}
      business={business}
      allowedNav={allowedNav}
      inboxUnread={inboxUnread}
      notifUnread={notifUnread}
    >
      {renderContent()}
    </AdminShell>
  );
};

export default AdminHome;
